import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetPaymentStatusMutation } from "../services/signup/registration";
import { useTranslation } from "react-i18next";
import PaymentStatusContent from "../components/signup/PaymentStatusContent";

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("signup");
  const [triggerVerify] = useGetPaymentStatusMutation();

  const [status, setStatus] = useState("processing");
  const [invoiceDetails, setInvoiceDetails] = useState(null);

  const pollingRef = useRef(null);
  const attemptsRef = useRef(0);
  const isCheckingRef = useRef(false);

  const MAX_ATTEMPTS = 20;
  const trackingToken = searchParams.get("verification_token");

  const checkPayment = useCallback(async () => {
    if (!trackingToken || isCheckingRef.current) return;

    isCheckingRef.current = true;

    try {
      const result = await triggerVerify({ token: trackingToken }).unwrap();
      const subscriptionStatus = result.subscription_status;

      if (subscriptionStatus === "completed") {
        clearInterval(pollingRef.current);
        setStatus("success");
        setInvoiceDetails(result?.invoice_details);
        toast.success(t("payment_verified_success"));
      }
      else if (subscriptionStatus === "payment_failed") {
        clearInterval(pollingRef.current);
        setStatus("error");
        setInvoiceDetails(result?.invoice_details);
        toast.error(result.error || t("payment_failed_note"));
      }
      else {
        setInvoiceDetails(result?.invoice_details);
        setStatus("processing");
      }
    } catch (err) {
      if (err?.status === 402) {
        clearInterval(pollingRef.current);
        setStatus("error");
        setInvoiceDetails(err?.data?.invoice_details);
        toast.error(err?.data?.error || t("payment_failed_note"));
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [trackingToken, triggerVerify, navigate, t]);

  useEffect(() => {
    if (!trackingToken) {
      setStatus("error");
      toast.error(t("invalid_callback_params"));
      return;
    }

    checkPayment();

    pollingRef.current = setInterval(() => {
      attemptsRef.current += 1;

      if (attemptsRef.current >= MAX_ATTEMPTS) {
        clearInterval(pollingRef.current);
        setStatus("verification");
        return;
      }

      checkPayment();
    }, 3000);

    return () => clearInterval(pollingRef.current);
  }, [checkPayment, trackingToken, t]);

  return (
    <div className="auth-wrapper payment-status-wrapper">
      <PaymentStatusContent
        status={status}
        invoiceDetails={invoiceDetails}
        onTryAgain={() => {
          if (invoiceDetails?.plan_slug && trackingToken) {
            if (invoiceDetails?.invite_token) {
              navigate(`/business/register/${invoiceDetails.invite_token}?token=${trackingToken}`);
            } else {
              navigate(`/signup/${invoiceDetails.plan_slug}?token=${trackingToken}`);
            }
          } else {
            navigate("/");
          }
        }}
      />
    </div>
  );
};

export default PaymentCallback;